import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/config';
import { AnimatePresence, motion } from 'framer-motion';
import Navigation from './components/Navigation';
import Loading from './components/Loading';
import { preloadCriticalResources, measureWebVitals, optimizeViewport, registerServiceWorker } from './utils/performance';

// パフォーマンス最適化のためのコンポーネント遅延読み込み
const Hero = lazy(() => import('./components/Hero'));
const About = lazy(() => import('./components/About'));
const Menu = lazy(() => import('./components/Menu'));
// const Pricing = lazy(() => import('./components/Pricing'));
const Entertainment = lazy(() => import('./components/Entertainment'));
// const Events = lazy(() => import('./components/Events'));
const IrisPage = lazy(() => import('./components/IrisPage'));
const Reservation = lazy(() => import('./components/Reservation'));
const Contact = lazy(() => import('./components/Contact'));

// エラーバウンダリコンポーネント
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('アプリケーションエラー:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center px-4">
          <div className="text-center">
            <div className="mb-8">
              <svg className="w-16 h-16 text-[#ff8c00] mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">申し訳ございません</h2>
            <p className="text-gray-300 mb-6">
              技術的な問題が発生しました。<br />
              ページを再読み込みしてください。
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-[#ff8c00] hover:bg-[#ff6a00] text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              ページを再読み込み
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// スキップリンクコンポーネント
const SkipLink: React.FC = () => (
  <a 
    href="#main-content" 
    className="skip-link"
    onFocus={(e) => e.target.style.top = '1rem'}
    onBlur={(e) => e.target.style.top = '-4rem'}
  >
    メインコンテンツにスキップ
  </a>
);

// パフォーマンス最適化されたローディング
const OptimizedSuspense: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense 
    fallback={
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#ff8c00] mx-auto mb-4"></div>
          <p className="text-white text-lg">読み込み中...</p>
        </div>
      </div>
    }
  >
    {children}
  </Suspense>
);

// メインページコンポーネント - バーとしての基本要素を重視した構成
const MainPage: React.FC = () => (
  <main id="main-content" role="main" aria-label="メインコンテンツ">
    <OptimizedSuspense>
      <Hero />
    </OptimizedSuspense>
    <OptimizedSuspense>
      <About />
    </OptimizedSuspense>
    <OptimizedSuspense>
      <Entertainment />
    </OptimizedSuspense>
    <OptimizedSuspense>
      <Menu />
    </OptimizedSuspense>
    {/* <OptimizedSuspense>
      <Events />
    </OptimizedSuspense>
    <OptimizedSuspense>
      <Pricing />
    </OptimizedSuspense> */}
    <OptimizedSuspense>
      <Reservation />
    </OptimizedSuspense>
    <OptimizedSuspense>
      <Contact />
    </OptimizedSuspense>
  </main>
);

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // オンライン/オフライン状態の監視
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // アプリケーション初期化
  useEffect(() => {
    const initApp = async () => {
      try {
        // ビューポート最適化
        optimizeViewport();
        
        // クリティカルリソースのプリロード
        preloadCriticalResources();
        
        // Web Vitals測定（開発環境のみ）
        if (process.env.NODE_ENV === 'development') {
          measureWebVitals();
        }

        // メタデータの動的更新
        document.title = 'Fastiny Entertainment Bar | 豪華エンターテイメントバー';
        
        // 言語属性の設定
        document.documentElement.lang = i18n.language;
        
        // Service Worker の登録（本番環境のみ）
        if (process.env.NODE_ENV === 'production') {
          await registerServiceWorker();
        }
        
      } catch (error) {
        console.error('アプリケーション初期化エラー:', error);
      }
    };

    initApp();
  }, []);

  // 言語変更時のメタデータ更新
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      document.documentElement.lang = lng;
      
      if (lng === 'en') {
        document.title = 'Fastiny Entertainment Bar | Luxury Entertainment Bar';
      } else {
        document.title = 'Fastiny Entertainment Bar | 豪華エンターテイメントバー';
      }
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  // オフライン通知
  const OfflineNotification = () => (
    <AnimatePresence>
      {!isOnline && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg z-50"
        >
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-12.728 12.728m0-12.728l12.728 12.728" />
            </svg>
            インターネット接続がありません
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <Router>
          <div className="App">
            <SkipLink />
            <OfflineNotification />
            
            <Loading onComplete={() => setIsLoading(false)} />
            
            <AnimatePresence mode="wait">
              {!isLoading && (
                <motion.div
                  key="content"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Navigation />
                  
                  <Routes>
                    <Route path="/" element={<MainPage />} />
                    <Route 
                      path="/iris" 
                      element={
                        <OptimizedSuspense>
                          <IrisPage />
                        </OptimizedSuspense>
                      } 
                    />
                    {/* 404ページ */}
                    <Route 
                      path="*" 
                      element={
                        <div className="min-h-screen bg-black flex items-center justify-center px-4">
                          <div className="text-center">
                            <h1 className="text-6xl font-bold text-[#ff8c00] mb-4">404</h1>
                            <h2 className="text-2xl font-bold text-white mb-4">ページが見つかりません</h2>
                            <p className="text-gray-300 mb-6">
                              お探しのページは存在しないか、移動した可能性があります。
                            </p>
                            <motion.a
                              href="/"
                              className="inline-block bg-[#ff8c00] hover:bg-[#ff6a00] text-white px-6 py-3 rounded-lg font-medium transition-colors"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              ホームに戻る
                            </motion.a>
                          </div>
                        </div>
                      } 
                    />
                  </Routes>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Router>
      </I18nextProvider>
    </ErrorBoundary>
  );
}

export default App;
